<template>
  <div>
    <!-- Page Header -->
    <CRow class="mb-3">
      <CCol>
        <h2>Tax Management Dashboard</h2>
        <p class="text-muted">Malaysian SST (Sales and Service Tax) Overview</p>
      </CCol>
      <CCol class="text-right">
        <CButton color="primary" @click="refreshDashboard" :disabled="loading">
          <CIcon name="cil-reload" /> Refresh
        </CButton>
        <CButton color="info" class="ml-2" @click="$router.push('/tenants/tax/periods')">
          <CIcon name="cil-list" /> View All Periods
        </CButton>
      </CCol>
    </CRow>

    <!-- Loading State -->
    <CRow v-if="loading">
      <CCol>
        <CCard>
          <CCardBody class="text-center py-5">
            <CSpinner color="primary" />
            <p class="mt-3">Loading tax dashboard...</p>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Error State -->
    <CRow v-else-if="error">
      <CCol>
        <CAlert color="danger">
          <h4 class="alert-heading">Error Loading Dashboard</h4>
          <p>{{ error }}</p>
          <hr>
          <CButton color="danger" variant="outline" @click="refreshDashboard">
            <CIcon name="cil-reload" /> Try Again
          </CButton>
        </CAlert>
      </CCol>
    </CRow>

    <!-- Dashboard Content -->
    <template v-else>
      <!-- Current Period Card -->
      <CRow v-if="currentPeriod">
        <CCol md="12">
          <CCard accent-color="primary">
            <CCardHeader class="d-flex justify-content-between align-items-center">
              <strong>Current Tax Period: {{ currentPeriod.periodCode }}</strong>
              <CBadge :color="getUrgencyColor(currentPeriod)">
                {{ getUrgencyText(currentPeriod) }}
              </CBadge>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <div class="text-center">
                    <div class="text-muted small">Output Tax (Sales)</div>
                    <div class="h3 text-danger">{{ formatCurrency(currentPeriod.totalOutputTax) }}</div>
                    <div class="text-muted small">{{ currentPeriod.invoiceCount || 0 }} invoices</div>
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="text-center">
                    <template v-if="deductsInputTax(currentPeriod.totalInputTax)">
                      <div class="text-muted small">Input tax (claimable)</div>
                      <div class="h3 text-success">{{ formatCurrency(currentPeriod.totalInputTax) }}</div>
                    </template>
                    <template v-else>
                      <div class="text-muted small">
                        Input tax (purchases)
                        <CIcon
                          name="cil-info"
                          size="sm"
                          class="ml-1"
                          tabindex="0"
                          :aria-label="notClaimableHint"
                          v-c-tooltip="{ content: notClaimableHint, placement: 'top' }"
                        />
                      </div>
                      <div class="h3 text-muted">Not claimable</div>
                      <div v-if="currentInputTaxPaid != null" class="text-muted small">
                        {{ formatCurrency(currentInputTaxPaid) }} SST paid
                      </div>
                    </template>
                    <div class="text-muted small">{{ currentPeriod.billCount || 0 }} bills</div>
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="text-center">
                    <div class="text-muted small">Net Tax Payable</div>
                    <div class="h3" :class="currentPeriod.netTaxPayable < 0 ? 'text-success' : 'text-primary'">
                      {{ formatCurrency(Math.abs(currentPeriod.netTaxPayable)) }}
                      <span v-if="currentPeriod.netTaxPayable < 0" class="small">(Refundable)</span>
                    </div>
                    <div class="text-muted small">{{ currentPeriod.transactionCount || 0 }} transactions</div>
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="text-center">
                    <div class="text-muted small">Due Date</div>
                    <div class="h4">{{ formatDate(currentPeriod.dueDate) }}</div>
                    <div class="text-muted small">{{ getDaysText(currentPeriod.dueDate) }}</div>
                  </div>
                </CCol>
              </CRow>
              <hr>
              <div class="text-right">
                <CButton
                  color="info"
                  size="sm"
                  @click="viewPeriodDetails(currentPeriod.id)"
                >
                  <CIcon name="cil-file" /> View Details
                </CButton>
                <CButton
                  color="primary"
                  size="sm"
                  class="ml-2"
                  @click="downloadSST03(currentPeriod.id)"
                >
                  <CIcon name="cil-cloud-download" /> Download SST-03
                </CButton>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <!-- Overdue Periods Alert -->
      <CRow v-if="overdue && overdue.length > 0">
        <CCol>
          <CAlert color="danger" class="d-flex justify-content-between align-items-center">
            <div>
              <CIcon name="cil-warning" class="mr-2" />
              <strong>{{ overdue.length }} Overdue Period{{ overdue.length > 1 ? 's' : '' }}</strong> - Immediate action required
            </div>
            <CButton color="danger" variant="outline" size="sm" @click="showOverdueDetails = !showOverdueDetails">
              {{ showOverdueDetails ? 'Hide' : 'Show' }} Details
            </CButton>
          </CAlert>

          <CCard v-if="showOverdueDetails" class="border-danger">
            <CCardHeader class="bg-danger text-white">
              <strong>Overdue Periods</strong>
            </CCardHeader>
            <CCardBody>
              <CListGroup>
                <CListGroupItem
                  v-for="period in overdue"
                  :key="period.id"
                  class="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <strong>{{ period.periodCode }}</strong> - Due: {{ formatDate(period.dueDate) }}
                    <br>
                    <small class="text-muted">
                      Net Payable: {{ formatCurrency(period.netTaxPayable) }} |
                      {{ period.transactionCount }} transactions
                    </small>
                  </div>
                  <div>
                    <CBadge color="danger">{{ Math.abs(getDaysUntilDue(period.dueDate)) }} days overdue</CBadge>
                    <CButton
                      color="primary"
                      size="sm"
                      class="ml-2"
                      @click="viewPeriodDetails(period.id)"
                    >
                      View
                    </CButton>
                  </div>
                </CListGroupItem>
              </CListGroup>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <!-- Upcoming Due Periods -->
      <CRow v-if="upcomingDue && upcomingDue.length > 0">
        <CCol md="12">
          <CCard>
            <CCardHeader>
              <strong>Upcoming Tax Obligations (Next 30 Days)</strong>
            </CCardHeader>
            <CCardBody>
              <CDataTable
                :items="upcomingDue"
                :fields="upcomingFields"
                hover
                striped
                clickable-rows
                @row-clicked="viewPeriodDetails"
              >
                <template #periodCode="{item}">
                  <td>
                    <strong>{{ item.periodCode }}</strong>
                  </td>
                </template>
                <template #netTaxPayable="{item}">
                  <td :class="item.netTaxPayable < 0 ? 'text-success' : 'text-danger'">
                    {{ formatCurrency(Math.abs(item.netTaxPayable)) }}
                    <span v-if="item.netTaxPayable < 0" class="small">(Refundable)</span>
                  </td>
                </template>
                <template #dueDate="{item}">
                  <td>
                    {{ formatDate(item.dueDate) }}
                    <br>
                    <CBadge :color="getDaysUntilDue(item.dueDate) <= 7 ? 'warning' : 'info'">
                      {{ getDaysText(item.dueDate) }}
                    </CBadge>
                  </td>
                </template>
                <template #actions="{item}">
                  <td class="text-right">
                    <CButton
                      color="primary"
                      size="sm"
                      @click.stop="downloadSST03(item.id)"
                    >
                      SST-03
                    </CButton>
                  </td>
                </template>
              </CDataTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <!-- Year to Date Summary -->
      <CRow>
        <CCol md="6">
          <CCard>
            <CCardHeader>
              <strong>Year to Date Summary ({{ new Date().getFullYear() }})</strong>
            </CCardHeader>
            <CCardBody>
              <CRow class="text-center">
                <CCol md="6" class="mb-3">
                  <div class="text-muted small">Total Output Tax</div>
                  <div class="h3 text-danger">{{ formatCurrency(ytdSummary.totalOutputTax || 0) }}</div>
                </CCol>
                <CCol md="6" class="mb-3">
                  <template v-if="deductsInputTax(ytdSummary.totalInputTax)">
                    <div class="text-muted small">Input tax (claimable)</div>
                    <div class="h3 text-success">{{ formatCurrency(ytdSummary.totalInputTax || 0) }}</div>
                  </template>
                  <template v-else>
                    <div class="text-muted small">
                      Input tax (purchases)
                      <CIcon
                        name="cil-info"
                        size="sm"
                        class="ml-1"
                        tabindex="0"
                        :aria-label="notClaimableHint"
                        v-c-tooltip="{ content: notClaimableHint, placement: 'top' }"
                      />
                    </div>
                    <div class="h3 text-muted">Not claimable</div>
                    <div v-if="ytdInputTaxPaid != null" class="text-muted small">
                      {{ formatCurrency(ytdInputTaxPaid) }} SST paid
                    </div>
                  </template>
                </CCol>
                <CCol md="6">
                  <div class="text-muted small">Net Tax Payable</div>
                  <div class="h3 text-primary">{{ formatCurrency(ytdSummary.netTaxPayable || 0) }}</div>
                </CCol>
                <CCol md="6">
                  <div class="text-muted small">Periods Filed</div>
                  <div class="h3">{{ ytdSummary.periodCount || 0 }}</div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol md="6">
          <CCard>
            <CCardHeader>
              <strong>Quick Actions</strong>
            </CCardHeader>
            <CCardBody>
              <CListGroup>
                <CListGroupItem
                  action
                  @click="$router.push('/tenants/tax/periods')"
                  class="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <CIcon name="cil-list" class="mr-2" />
                    <strong>View All Tax Periods</strong>
                    <br>
                    <small class="text-muted">Browse and manage all tax periods</small>
                  </div>
                  <CIcon name="cil-chevron-right" />
                </CListGroupItem>

                <CListGroupItem
                  action
                  @click="createNewPeriod"
                  class="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <CIcon name="cil-plus" class="mr-2" />
                    <strong>Create New Period</strong>
                    <br>
                    <small class="text-muted">Manually create a tax period</small>
                  </div>
                  <CIcon name="cil-chevron-right" />
                </CListGroupItem>

                <CListGroupItem
                  action
                  @click="$router.push('/tenants/invoices')"
                  class="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <CIcon name="cil-file" class="mr-2" />
                    <strong>View Invoices</strong>
                    <br>
                    <small class="text-muted">Output tax (sales)</small>
                  </div>
                  <CIcon name="cil-chevron-right" />
                </CListGroupItem>

                <CListGroupItem
                  action
                  @click="$router.push('/tenants/bills')"
                  class="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <CIcon name="cil-notes" class="mr-2" />
                    <strong>View Bills</strong>
                    <br>
                    <small class="text-muted">Input tax (purchases)</small>
                  </div>
                  <CIcon name="cil-chevron-right" />
                </CListGroupItem>
              </CListGroup>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </template>
  </div>
</template>

<script>
import TaxAPI from '@/lib/taxApi'
import { formatMyr } from '@/utils/money'

export default {
  name: 'TaxDashboard',
  data() {
    return {
      loading: true,
      error: null,
      currentPeriod: null,
      upcomingDue: [],
      overdue: [],
      ytdSummary: {},
      // From the dashboard response; null until loaded (then the screen falls back to the old display)
      inputTaxClaimable: null,
      currentInputTaxPaid: null,
      ytdInputTaxPaid: null,
      notClaimableHint: "SST paid on purchases is part of your cost and can't be deducted. Ask your tax agent if your business qualifies, then change it in Business settings.",
      showOverdueDetails: false,
      upcomingFields: [
        { key: 'periodCode', label: 'Period' },
        { key: 'netTaxPayable', label: 'Net Payable' },
        { key: 'transactionCount', label: 'Transactions' },
        { key: 'dueDate', label: 'Due Date' },
        { key: 'actions', label: '', _style: 'width:100px' }
      ]
    }
  },
  async mounted() {
    this.$store.commit("setPageNavItems", ["Tax Management", "Dashboard"])
    await this.loadDashboard()
  },
  methods: {
    async loadDashboard() {
      this.loading = true
      this.error = null

      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))
        const response = await taxApi.getDashboard()

        const data = response.data.result
        this.currentPeriod = data.currentPeriod
        this.upcomingDue = data.upcomingDue || []
        this.overdue = data.overdue || []
        this.ytdSummary = data.yearToDateSummary || {}

        // SST input tax setting and SST paid come with the dashboard (docs/ACCOUNTING-POSTING.md)
        this.inputTaxClaimable = typeof data.inputTaxClaimable === 'boolean' ? data.inputTaxClaimable : null
        this.currentInputTaxPaid = this.currentPeriod ? (this.currentPeriod.inputTaxPaid || 0) : null
        this.ytdInputTaxPaid = this.ytdSummary.inputTaxPaid != null ? this.ytdSummary.inputTaxPaid : null

        // Auto-show overdue details if there are overdue periods
        if (this.overdue.length > 0) {
          this.showOverdueDetails = true
        }
      } catch (error) {
        console.error('Error loading tax dashboard:', error)
        this.error = error.response?.data?.message || error.message || 'Failed to load dashboard'
      } finally {
        this.loading = false
      }
    },

    // Show input tax as a deduction when claimable (or unknown), or when the API's total still deducts it
    deductsInputTax(totalInputTax) {
      return this.inputTaxClaimable !== false || (parseFloat(totalInputTax) || 0) !== 0
    },

    async refreshDashboard() {
      await this.loadDashboard()
      this.$toast.success('Dashboard refreshed')
    },

    viewPeriodDetails(periodOrId) {
      const id = typeof periodOrId === 'string' ? periodOrId : periodOrId.id
      this.$router.push(`/tenants/tax/periods/${id}`)
    },

    async downloadSST03(periodId) {
      try {
        this.$router.push(`/tenants/tax/sst03/${periodId}`)
      } catch (error) {
        console.error('Error navigating to SST-03:', error)
        this.$toast.error("Couldn't open the SST-03 report. Check your connection and try again.")
      }
    },

    async createNewPeriod() {
      // TODO: Show modal to select year/month
      this.$router.push('/tenants/tax/periods')
    },

    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`
    },

    formatDate(date) {
      const taxApi = new TaxAPI()
      return taxApi.formatDate(date)
    },

    getDaysUntilDue(dueDate) {
      const taxApi = new TaxAPI()
      return taxApi.getDaysUntilDue(dueDate)
    },

    getDaysText(dueDate) {
      const days = this.getDaysUntilDue(dueDate)
      if (days < 0) {
        return `${Math.abs(days)} days overdue`
      } else if (days === 0) {
        return 'Due today'
      } else if (days === 1) {
        return 'Due tomorrow'
      } else {
        return `${days} days remaining`
      }
    },

    getUrgencyColor(period) {
      const taxApi = new TaxAPI()
      const badge = taxApi.getUrgencyBadge(period)
      return badge.color
    },

    getUrgencyText(period) {
      const taxApi = new TaxAPI()
      const badge = taxApi.getUrgencyBadge(period)
      return badge.text
    }
  }
}
</script>

<style scoped>
.text-right {
  text-align: right;
}
</style>